import {eq, isEmpty, isEqual, isNil} from '@shared/helpers/lang'
import {mapValueKeys} from '@shared/helpers/object'

import {validateCoupon, validateCouponList} from '@web/actions/orderSheetDiscount'
import {initOrderSheetByStorage} from '@web/actions/orderSheetInfo'
import {DiscountItem, getCoupons, updateDeliveryCoupons} from '@web/actions/productCoupon'
import {APIStatus, DiscountCoupon, DiscountCouponType} from '@web/core/constants'
import {
    OrderSheetCoupon,
    OrderSheetCouponState,
    getDeliveryCoupons,
    getOptimizedDeliveryCoupon,
} from '@web/slices/orderSheetCoupon/slice'
import makeSlice from '@web/slices/orderSheetCoupon/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'
import {Coupon, ProductCouponListsByOrderSheetItemId} from '../../apis/interface/response/interface'
import {convertDeliveryGroupsByKey} from '../../utils/delivery'

const getProductCouponType = (coupon: Pick<Coupon, 'couponDiscountType'>): DiscountCouponType => {
    switch (coupon.couponDiscountType) {
        case 'MERCHANT_PRODUCT_DISCOUNT':
            return DiscountCoupon.SELLER_PRODUCT_DISCOUNT
        case 'MANAGER_PRODUCT_DISCOUNT':
            return DiscountCoupon.MANAGER_PRODUCT_DISCOUNT
        case 'DUPLICATE_PRODUCT_DISCOUNT':
            return DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT
        case 'DELIVERY_FEE_DISCOUNT':
            return DiscountCoupon.DELIVERY_DISCOUNT
    }
    return DiscountCoupon.ORDER_DISCOUNT
}

function convertDiscountInfo(
    discountsByItemId: Record<string, DiscountItem[]>,
): OrderSheetCouponState['discount']['productItem'] {
    return mapValueKeys((discounts) => {
        return discounts.map(
            ({
                baseAmount,
                discountAmount,
                discountType,
                discountProviderType,
                discountUnitType,
                discountValue,
                validityPeriodEndDateTime,
                multiPurchaseUnitType,
                multiPurchaseMinOrderValue,
            }) => ({
                baseAmount,
                discountAmount,
                discountType,
                discountProviderType,
                couponType: getProductCouponType({
                    couponDiscountType: discountType,
                }),
                discountUnitType,
                discountValue,
                validityPeriodEndDateTime,
                multiPurchaseUnitType,
                multiPurchaseMinOrderValue,
            }),
        )
    }, discountsByItemId)
}

/**
 * 상품 쿠폰은 상품쿠폰 / 중복할인 쿠폰으로 나뉘어 들어간다.
 * C3의 이미 선택된 쿠폰은 상품쿠폰이 나뉘지 않고 들어간다.
 * 따라서 filtering 방식이 중복할인 쿠폰이거나, 아니거나, 필터링하지 않거나(ALL)로 나뉜다
 */
export const convertProductCoupon = ({
    coupon,
    duplicatedOnly,
}: {
    coupon: ProductCouponListsByOrderSheetItemId<Coupon>
    duplicatedOnly: boolean
}): OrderSheetCouponState['product']['couponsByOrderSheetItemId'] => {
    return mapValueKeys((couponItems) => {
        return couponItems
            .map((item) => {
                return {
                    ...item,
                    couponType: getProductCouponType(item),
                    disabled: false,
                }
            })
            .filter(({couponType}) => {
                const isDuplicatedCoupon = couponType === DiscountCoupon.DUPLICATE_PRODUCT_DISCOUNT
                return duplicatedOnly === isDuplicatedCoupon
            })
            .sort((couponA, couponB) => (couponA.couponId > couponB.couponId ? 1 : -1))
    }, coupon)
}

function filterProductCoupon<S, C>({
    selectedCoupons,
    couponsByOrderSheetItemId,
    someFunction,
}: {
    selectedCoupons: {[OrderSheetItemId: string]: S[]}
    couponsByOrderSheetItemId: {[OrderSheetItemId: string]: C[]}
    someFunction: (coupon: C, selectedCoupon: S) => boolean
}) {
    return mapValueKeys((coupons, orderSheetItemId) => {
        return coupons.filter((selectedCoupon) => {
            return couponsByOrderSheetItemId[orderSheetItemId]?.some((coupon) => someFunction(coupon, selectedCoupon))
        })
    }, selectedCoupons)
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initOrderSheetByStorage.fulfilled, (state, {payload}) => {
                const {
                    data: {orderSheetCoupon},
                } = payload

                if (eq(state.couponStatus, 'default')) {
                    // 쿠폰 복원시 보유한 쿠폰 내에서만 복원 시도
                    state.selected.productCoupon = {
                        ...filterProductCoupon<OrderSheetCoupon, OrderSheetCoupon>({
                            selectedCoupons: orderSheetCoupon.selected.productCoupon,
                            couponsByOrderSheetItemId: state.product.couponsByOrderSheetItemId,
                            someFunction: ({couponId}, coupon) => couponId === coupon.couponId,
                        }),
                    }
                    state.selected.duplicateCoupon = {
                        ...filterProductCoupon<OrderSheetCoupon, OrderSheetCoupon>({
                            selectedCoupons: orderSheetCoupon.selected.duplicateCoupon,
                            couponsByOrderSheetItemId: state.product.duplicateCouponsByOrderSheetItemId,
                            someFunction: ({couponId}, coupon) => couponId === coupon.couponId,
                        }),
                    }
                    // 첫주문일경우 배송지를 등록하고, 배송쿠폰을 복원하면, 쿠폰이 없다가 생겨난다.
                    // 때문에 배송 쿠폰은 그대로 복원하고, 검증 여부를 selector 에서 처리한다.
                    state.selected.deliveryCoupon = orderSheetCoupon.selected.deliveryCoupon
                    state.discount = orderSheetCoupon.discount

                    state.beforeApplyTemporaryCoupon = orderSheetCoupon.beforeApplyCoupon
                }
            })
            .addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
                const {
                    delivery,
                    coupon,
                    payMethod: {partnershipCardCodes},
                } = payload

                // easybooking 스펙
                // 혜택 카드가 있으면 쿠폰 미적용
                if (!isEmpty(partnershipCardCodes)) {
                    return
                }

                state.couponStatus = coupon.fallback ? 'failed' : 'default'

                const DeliveryGroupsByKey = convertDeliveryGroupsByKey(delivery.deliveryGroupsByKey)
                const CouponsByDeliveryGroupKey = mapValueKeys((couponItems) => {
                    return couponItems.map((item) => {
                        return {
                            ...item,
                            couponType: DiscountCoupon.DELIVERY_DISCOUNT,
                            disabled: false,
                        }
                    })
                }, coupon.deliveryFeeCoupons.deliveryFeeCouponListsByDeliveryGroupKey)

                const DeliveryCoupons: OrderSheetCoupon[] = getDeliveryCoupons({
                    couponsByDeliveryGroupKey: CouponsByDeliveryGroupKey,
                })

                const OptimizedDeliveryCoupon = getOptimizedDeliveryCoupon({
                    couponsByDeliveryGroupKey: CouponsByDeliveryGroupKey,
                    deliveryGroupsByKey: DeliveryGroupsByKey,
                    deliveryCoupons: DeliveryCoupons,
                })

                if (coupon.deliveryFeeCoupons.usableDeliveryFeeCouponCount === 0 || isEmpty(DeliveryCoupons)) {
                    state.deliveryFee.isOptimized = true
                }

                if (OptimizedDeliveryCoupon) {
                    state.optimizedCoupon.deliveryCoupon = OptimizedDeliveryCoupon
                    state.deliveryFee.isOptimized = true
                    state.selected.deliveryCoupon = OptimizedDeliveryCoupon
                }

                state.deliveryFee.couponsByDeliveryGroupKey = CouponsByDeliveryGroupKey
                state.deliveryFee.usableCount = DeliveryCoupons.length
                state.deliveryFee.validCount = coupon.deliveryFeeCoupons.validDeliveryFeeCouponCount

                if (coupon.deliveryFeeCoupons.usableDeliveryFeeCouponCount === 0) {
                    state.deliveryFee.isOptimized = true
                }

                state.product.usableCount = coupon.productCoupons.usableProductCouponCount
                state.product.validCount = coupon.productCoupons.validProductCouponCount

                state.discountOptimization = coupon.discountOptimization

                // 상품 쿠폰
                state.product.couponsByOrderSheetItemId = convertProductCoupon({
                    coupon: coupon.productCoupons.productCouponListsByOrderSheetItemId,
                    duplicatedOnly: false,
                })

                // 중복할인 쿠폰
                state.product.duplicateCouponsByOrderSheetItemId = convertProductCoupon({
                    coupon: coupon.productCoupons.productCouponListsByOrderSheetItemId,
                    duplicatedOnly: true,
                })

                state.discountOptimization = coupon.discountOptimization

                if (coupon.selectedProductCouponListsByOrderSheetItemId) {
                    const OptimizedProductCoupon = convertProductCoupon({
                        coupon: coupon.selectedProductCouponListsByOrderSheetItemId,
                        duplicatedOnly: false,
                    })
                    const OptimizedDuplicateCoupon = convertProductCoupon({
                        coupon: coupon.selectedProductCouponListsByOrderSheetItemId,
                        duplicatedOnly: true,
                    })

                    state.selected.productCoupon = OptimizedProductCoupon
                    state.selected.duplicateCoupon = OptimizedDuplicateCoupon
                    state.optimizedCoupon.productCoupon = OptimizedProductCoupon
                    state.optimizedCoupon.duplicateCoupon = OptimizedDuplicateCoupon
                }

                // 할인 금액 정보
                state.discount.productItem = convertDiscountInfo(coupon.discountsByItemId)
            })
            .addCase(getCoupons.fulfilled, (state, {payload}) => {
                state.apiStatus = APIStatus.FETCHING

                const {coupons, deliveryGroupsByKey} = payload

                const {
                    selectedProductCouponListsByOrderSheetItemId,
                    productCoupons,
                    deliveryFeeCoupons,
                    discountOptimization,
                    fallback,
                } = coupons

                state.couponStatus = fallback ? 'failed' : 'default'

                state.discountOptimization = discountOptimization

                const CouponsByDeliveryGroupKey = mapValueKeys((couponItems) => {
                    return couponItems.map((item) => {
                        return {
                            ...item,
                            couponType: DiscountCoupon.DELIVERY_DISCOUNT,
                            disabled: false,
                        }
                    })
                }, deliveryFeeCoupons.deliveryFeeCouponListsByDeliveryGroupKey)

                const DeliveryGroupsByKey = convertDeliveryGroupsByKey(deliveryGroupsByKey)

                const DeliveryCoupons: OrderSheetCoupon[] = getDeliveryCoupons({
                    couponsByDeliveryGroupKey: CouponsByDeliveryGroupKey,
                })

                const OptimizedDeliveryCoupon = getOptimizedDeliveryCoupon({
                    couponsByDeliveryGroupKey: CouponsByDeliveryGroupKey,
                    deliveryGroupsByKey: DeliveryGroupsByKey,
                    deliveryCoupons: DeliveryCoupons,
                })

                /** 변경된 배송비쿠폰목록 반영 */
                state.deliveryFee.couponsByDeliveryGroupKey = CouponsByDeliveryGroupKey
                state.deliveryFee.usableCount = deliveryFeeCoupons.usableDeliveryFeeCouponCount
                state.deliveryFee.validCount = deliveryFeeCoupons.validDeliveryFeeCouponCount

                /**
                 * 사용가능 배송비쿠폰이 없음 or 받아온 배송비쿠폰이 없다면
                 * 배송비 최적혜택이 적용중인걸로 간주
                 */
                if (deliveryFeeCoupons.usableDeliveryFeeCouponCount === 0 || isEmpty(DeliveryCoupons)) {
                    state.deliveryFee.isOptimized = true
                }

                if (OptimizedDeliveryCoupon) {
                    state.deliveryFee.isOptimized = true
                    state.optimizedCoupon.deliveryCoupon = OptimizedDeliveryCoupon
                    state.beforeApplyCoupon.deliveryCoupon = OptimizedDeliveryCoupon
                }

                /**
                 * 쿠폰 반영 Section 정책
                 *
                 * 1. fallback true case > 혜택쿠폰 조회 실패 case 에도 받아온 쿠폰 그대로 적용
                 * fallback true (혜택쿠폰조회 실패 case) 에서는 혜택계산기를 조회하지 않아
                 * discountOptimization 필드는 항상 false로 내려오므로 fallback 정책이 우선.
                 * 어차피 즉시할인 쿠폰만 내려받고 선택해주므르 그대로 반영한다.
                 *
                 * 2. 최적혜택 적용시에 적용
                 */
                if (fallback || discountOptimization) {
                    state.product.usableCount = productCoupons.usableProductCouponCount
                    state.product.validCount = productCoupons.validProductCouponCount
                    state.deliveryFee.validCount = deliveryFeeCoupons.validDeliveryFeeCouponCount

                    const ProductCoupons = convertProductCoupon({
                        coupon: productCoupons.productCouponListsByOrderSheetItemId,
                        duplicatedOnly: false,
                    })
                    const DuplicatedCoupons = convertProductCoupon({
                        coupon: productCoupons.productCouponListsByOrderSheetItemId ?? {},
                        duplicatedOnly: true,
                    })

                    if (
                        !isEqual(state.product.couponsByOrderSheetItemId, ProductCoupons) ||
                        !isEqual(state.product.duplicateCouponsByOrderSheetItemId, DuplicatedCoupons) ||
                        !isEqual(state.deliveryFee.couponsByDeliveryGroupKey, CouponsByDeliveryGroupKey)
                    ) {
                        state.couponPageType = 'updated'
                    }
                    if (
                        !validateCoupon({
                            selected: state.selected.deliveryCoupon,
                            coupons: CouponsByDeliveryGroupKey,
                        }) ||
                        !validateCouponList({
                            selected: state.selected.productCoupon,
                            coupons: ProductCoupons,
                        }) ||
                        !validateCouponList({
                            selected: state.selected.duplicateCoupon,
                            coupons: DuplicatedCoupons,
                        })
                    ) {
                        state.couponPageType = 'blocked'
                        state.selected.deliveryCoupon = {}
                        state.selected.productCoupon = {}
                        state.selected.duplicateCoupon = {}
                    }

                    /** 변경된 쿠폰목록 반영 */
                    state.product.couponsByOrderSheetItemId = ProductCoupons
                    state.product.duplicateCouponsByOrderSheetItemId = DuplicatedCoupons

                    if (!isNil(selectedProductCouponListsByOrderSheetItemId)) {
                        const OptimizedProductCoupon = convertProductCoupon({
                            coupon: selectedProductCouponListsByOrderSheetItemId,
                            duplicatedOnly: false,
                        })
                        const OptimizedDuplicateCoupon = convertProductCoupon({
                            coupon: selectedProductCouponListsByOrderSheetItemId,
                            duplicatedOnly: true,
                        })
                        state.beforeApplyCoupon.productCoupon = OptimizedProductCoupon
                        state.beforeApplyTemporaryCoupon.productCoupon = OptimizedProductCoupon
                        state.optimizedCoupon.productCoupon = OptimizedProductCoupon

                        state.beforeApplyCoupon.duplicateCoupon = OptimizedDuplicateCoupon
                        state.beforeApplyTemporaryCoupon.duplicateCoupon = OptimizedDuplicateCoupon
                        state.optimizedCoupon.duplicateCoupon = OptimizedDuplicateCoupon
                    }
                } else {
                    /**
                     * 혜택계산기 조회 실패해도
                     * 1. 배송비는 최적적용으로 적용한다.
                     * 2. selected 쿠폰은 빈 값으로 내려오므로 받아온 쿠폰리스트도 함께 무시한다.
                     **/
                    state.beforeApplyCoupon.deliveryCoupon = state.optimizedCoupon.deliveryCoupon
                }
            })
            .addCase(updateDeliveryCoupons.fulfilled, (state, {payload}) => {
                const {
                    deliveryCoupons: {deliveryFeeCouponListsByDeliveryGroupKey, usableDeliveryFeeCouponCount},
                    deliveryGroupsByKey,
                    shouldDeliveryCouponOptimize,
                } = payload

                const CouponsByDeliveryGroupKey = mapValueKeys((couponItems) => {
                    return couponItems.map((item) => {
                        return {
                            ...item,
                            couponType: DiscountCoupon.DELIVERY_DISCOUNT,
                            disabled: false,
                        }
                    })
                }, deliveryFeeCouponListsByDeliveryGroupKey)

                const DeliveryGroupsByKey = convertDeliveryGroupsByKey(deliveryGroupsByKey)

                const DeliveryCoupons: OrderSheetCoupon[] = getDeliveryCoupons({
                    couponsByDeliveryGroupKey: CouponsByDeliveryGroupKey,
                })

                const OptimizedDeliveryCoupon = getOptimizedDeliveryCoupon({
                    couponsByDeliveryGroupKey: CouponsByDeliveryGroupKey,
                    deliveryGroupsByKey: DeliveryGroupsByKey,
                    deliveryCoupons: DeliveryCoupons,
                })

                /** 변경된 배송비쿠폰목록 반영 */
                state.deliveryFee.couponsByDeliveryGroupKey = CouponsByDeliveryGroupKey
                state.deliveryFee.usableCount = usableDeliveryFeeCouponCount

                if (usableDeliveryFeeCouponCount === 0 || isEmpty(DeliveryCoupons)) {
                    state.deliveryFee.isOptimized = true
                }

                if (shouldDeliveryCouponOptimize && OptimizedDeliveryCoupon) {
                    state.deliveryFee.isOptimized = true
                    state.optimizedCoupon.deliveryCoupon = OptimizedDeliveryCoupon
                    state.selected.deliveryCoupon = OptimizedDeliveryCoupon
                }
            })
    },
})

export default slice.reducer
